import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import Reaptcha from 'reaptcha';
import {
    Modal,
    Form,
    Input,
    Message,
    Icon,
    Label as BaseLabel,
} from 'semantic-ui-react';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';
import { FormLabel, Button, PhoneInput } from '../../../components';
import { login } from '../../../actions/user';
import withNewSalon from '../../../lib/withNewSalon';
import config from '../../../config';
import { get } from 'lodash';

const intlMessages = defineMessages({
    salonNameRequired: {
        id: 'pages.signup.form.salonNameError',
        defaultMessage: 'Enter salon name (or company name)',
    },
    salonAddressRequired: {
        id: 'pages.signup.form.salonAddressError',
        defaultMessage: 'Enter your salon address (or company address)',
    },
    firstNameRequired: {
        id: 'pages.signup.form.firstNameError',
        defaultMessage: 'Enter your first name (e.g. John Smith)',
    },
    emailRequired: {
        id: 'pages.signup.form.emailError',
        defaultMessage: 'Enter your email address',
    },
    passwordRequired: {
        id: 'pages.signup.form.passwordError',
        defaultMessage: 'Enter password for authetnication',
    },
    nameExistingsError: {
        id: 'pages.settings.generalSettings.nameExistingsError',
        defaultMessage: 'Salon with the same name already exists',
    },
    emailExistingsError: {
        id: 'pages.signup.form.nameExistingsError',
        defaultMessage:
            'Administrator with the same email address already exists',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

export const SIGNUP_MUTATION = gql`
    mutation signup($input: SignupAdministratorInput!) {
        signupAdministrator(input: $input) {
            token
            user {
                id
                firstName
                lastName
            }
            salon {
                id
            }
            role
        }
    }
`;

const StyledDialog = styled(Modal)`
    width: 500px !important;
    margin-top: 0 !important;
`;

const ModalActions = styled(Modal.Actions)`
    padding-left: 1.5em !important;
    padding-right: 1.5em !important;
`;

const SubmitButton = styled(Button)`
    margin: 0px !important;
    padding-top: 1em !important;
    padding-bottom: 1em !important;
    text-transform: uppercase !important;
`;

const DEFAULT_VALUES = {
    salonName: '',
    salonAddress: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    promoCode: '',
};

function SignupDialog({
    trigger,
    open,
    onClose,
    login: loginAction,
    history,
    setupNewSalon,
    setOwnerAsMaster,
    intl,
}) {
    const [captchaToken, setCaptchaToken] = useState(false);
    const [signupMutation] = useMutation(SIGNUP_MUTATION);
    const {
        control,
        handleSubmit,
        setError,
        formState: { errors, isSubmitting, isDirty },
    } = useForm({ defaultValues: DEFAULT_VALUES });

    const onSubmit = async formData => {
        try {
            const { data } = await signupMutation({
                variables: { input: { ...formData, token: captchaToken } },
            });
            loginAction({
                id: data.signupAdministrator.user.id,
                role: data.signupAdministrator.role,
                token: data.signupAdministrator.token,
                salon: data.signupAdministrator.salon.id,
                isOwner: true,
            });
            setupNewSalon(data.signupAdministrator.salon.id, () => {
                setOwnerAsMaster(
                    data.signupAdministrator.salon.id,
                    data.signupAdministrator.user.id,
                    () => {
                        history.push('/salon/appointments');
                    },
                );
            });
        } catch (error) {
            const graphQLError = (error.graphQLErrors || [])[0];
            if (graphQLError) {
                if (graphQLError.name === 'AlreadyExists') {
                    if (get(graphQLError, 'data.error') === 'NAME_ALREADY_EXISTS') {
                        setError('salonName', {
                            message: intl.formatMessage(
                                intlMessages.nameExistingsError,
                            ),
                        });
                        return;
                    } else if (
                        get(graphQLError, 'data.error') === 'EMAIL_ALREADY_EXISTS'
                    ) {
                        setError('email', {
                            message: intl.formatMessage(
                                intlMessages.emailExistingsError,
                            ),
                        });
                        return;
                    }
                }
                setError('root', {
                    message: intl.formatMessage(
                        intlMessages.defaultErrorMessage,
                    ),
                });
                return;
            }
            setError('root', {
                message: intl.formatMessage(intlMessages.defaultErrorMessage),
            });
        }
    };

    return (
        <StyledDialog trigger={trigger} open={open} onClose={onClose}>
            <Modal.Content>
                {errors.root && (
                    <Message error>
                        <Icon name="warning sign" />
                        {errors.root.message}
                    </Message>
                )}
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <Form.Field error={!!errors.salonName}>
                        <FormLabel>
                            <FormattedMessage
                                id="pages.signup.form.salonName"
                                defaultMessage="Salon name"
                            />
                        </FormLabel>
                        <Controller
                            name="salonName"
                            control={control}
                            rules={{
                                required: intl.formatMessage(
                                    intlMessages.salonNameRequired,
                                ),
                            }}
                            render={({ field: { ref: _ref, ...field } }) => (
                                <Input
                                    {...field}
                                    type="text"
                                    size="big"
                                    fluid
                                />
                            )}
                        />
                        {errors.salonName && (
                            <BaseLabel color="red" pointing basic>
                                {errors.salonName.message}
                            </BaseLabel>
                        )}
                    </Form.Field>
                    <Form.Field error={!!errors.salonAddress}>
                        <FormLabel>
                            <FormattedMessage
                                id="pages.signup.form.salonAddress"
                                defaultMessage="Salon address"
                            />
                        </FormLabel>
                        <Controller
                            name="salonAddress"
                            control={control}
                            rules={{
                                required: intl.formatMessage(
                                    intlMessages.salonAddressRequired,
                                ),
                            }}
                            render={({ field: { ref: _ref, ...field } }) => (
                                <Input
                                    {...field}
                                    type="text"
                                    size="big"
                                    fluid
                                />
                            )}
                        />
                        {errors.salonAddress && (
                            <BaseLabel color="red" pointing basic>
                                {errors.salonAddress.message}
                            </BaseLabel>
                        )}
                    </Form.Field>
                    <Form.Field error={!!errors.firstName}>
                        <FormLabel>
                            <FormattedMessage
                                id="pages.signup.form.firstName"
                                defaultMessage="Your first name"
                            />
                        </FormLabel>
                        <Controller
                            name="firstName"
                            control={control}
                            rules={{
                                required: intl.formatMessage(
                                    intlMessages.firstNameRequired,
                                ),
                            }}
                            render={({ field: { ref: _ref, ...field } }) => (
                                <Input
                                    {...field}
                                    type="text"
                                    size="big"
                                    fluid
                                />
                            )}
                        />
                        {errors.firstName && (
                            <BaseLabel color="red" pointing basic>
                                {errors.firstName.message}
                            </BaseLabel>
                        )}
                    </Form.Field>
                    <Form.Field>
                        <FormLabel>
                            <FormattedMessage
                                id="pages.signup.form.lastName"
                                defaultMessage="Your last name"
                            />
                        </FormLabel>
                        <Controller
                            name="lastName"
                            control={control}
                            render={({ field: { ref: _ref, ...field } }) => (
                                <Input
                                    {...field}
                                    type="text"
                                    size="big"
                                    fluid
                                />
                            )}
                        />
                    </Form.Field>
                    <Form.Field error={!!errors.email}>
                        <FormLabel>
                            <FormattedMessage
                                id="pages.signup.form.email"
                                defaultMessage="Your e-mail address"
                            />
                        </FormLabel>
                        <Controller
                            name="email"
                            control={control}
                            rules={{
                                required: intl.formatMessage(
                                    intlMessages.emailRequired,
                                ),
                            }}
                            render={({ field: { ref: _ref, ...field } }) => (
                                <Input
                                    {...field}
                                    type="text"
                                    size="big"
                                    fluid
                                />
                            )}
                        />
                        {errors.email && (
                            <BaseLabel color="red" pointing basic>
                                {errors.email.message}
                            </BaseLabel>
                        )}
                    </Form.Field>
                    <Form.Field>
                        <FormLabel>
                            <FormattedMessage
                                id="pages.signup.form.phone"
                                defaultMessage="Your phone number"
                            />
                        </FormLabel>
                        <Controller
                            name="phone"
                            control={control}
                            render={({ field: { ref: _ref, ...field } }) => (
                                <PhoneInput
                                    {...field}
                                    onChange={(e, data) =>
                                        field.onChange(data?.value ?? e)
                                    }
                                    size="big"
                                    fluid
                                />
                            )}
                        />
                    </Form.Field>
                    <Form.Field error={!!errors.password}>
                        <FormLabel>
                            <FormattedMessage
                                id="pages.signup.form.password"
                                defaultMessage="Password"
                            />
                        </FormLabel>
                        <Controller
                            name="password"
                            control={control}
                            rules={{
                                required: intl.formatMessage(
                                    intlMessages.passwordRequired,
                                ),
                            }}
                            render={({ field: { ref: _ref, ...field } }) => (
                                <Input
                                    {...field}
                                    type="password"
                                    size="big"
                                    fluid
                                />
                            )}
                        />
                        {errors.password && (
                            <BaseLabel color="red" pointing basic>
                                {errors.password.message}
                            </BaseLabel>
                        )}
                    </Form.Field>
                    <Form.Field>
                        <FormLabel>
                            <FormattedMessage
                                id="pages.signup.form.promoCode"
                                defaultMessage="Promo code (optional)"
                            />
                        </FormLabel>
                        <Controller
                            name="promoCode"
                            control={control}
                            render={({ field: { ref: _ref, ...field } }) => (
                                <Input
                                    {...field}
                                    type="text"
                                    size="big"
                                    fluid
                                />
                            )}
                        />
                    </Form.Field>
                    <div className="dimmer">
                        <Reaptcha
                            sitekey={config.recaptcha.key}
                            onVerify={t => setCaptchaToken(t)}
                        />
                    </div>
                </Form>
            </Modal.Content>
            <ModalActions>
                <SubmitButton
                    onClick={handleSubmit(onSubmit)}
                    disabled={isSubmitting || !isDirty || !captchaToken}
                    loading={isSubmitting}
                    primary
                    fluid
                >
                    <FormattedMessage
                        id="pages.signup.button.create"
                        defaultMessage="Create account"
                    />
                </SubmitButton>
            </ModalActions>
        </StyledDialog>
    );
}

export default withRouter(
    connect(
        null,
        { login },
    )(injectIntl(withNewSalon(SignupDialog))),
);
